import { Add, Edit } from "@mui/icons-material";
import { Button, DialogProps, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { map } from "rxjs";
import { UserTypeId, useAccountStore } from "../../common/account";
import { DialogPool, useDialog } from "../../common/dialog";
import { UseEntityStore } from "../../common/entityStore";
import { createListStore } from "../../common/listStore";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { StoreWrapper } from "../../common/store";
import { ContactDialog } from "./ContactDialog";
import { CustomerDto, CustomerNoteDto } from "./types";
import { createAxios } from "../../common/axios";

export function ContactList(props: { useCustomerStore: UseEntityStore<CustomerDto>; }) {
    const customerStore = props.useCustomerStore(p => ({
        serverData$: p.serverData$,
        id: p.serverData.id!, custId: p.serverData.custId,
        isNew: p.serverData.id == null,
    }));
    const customerId = customerStore.id;
    const [useNoteListStore] = useState(() => {
        return createListStore<CustomerNoteDto>({
            data: customerStore.serverData$.pipe(map(p => ({ items: p.data.customerNotes, isFetching: p.isFetching }))),
            itemsPerPage: 5, getId: p => p.id, listProps: [
                { column: "dte", label: "Date", width: 100, type: "date" },
                { column: "codes", label: "Type", width: 100 },
                { column: "txt", label: "Text", flex: 1 },
                { column: "userName", label: "User", width: 100 },
            ]
        });
    });

    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const canManage = accountStore.userTypeId !== UserTypeId.View && accountStore.userTypeId !== UserTypeId.CollectionAgency;
    const selectedNote = useNoteListStore(p => p.selectedItem);

    const dialogPool = useRef<DialogPool>({ openDialogs: 0 });

    const contactDialogProps: () => Partial<DialogProps> = () => ({
        hideBackdrop: true,
        disableEnforceFocus: true,
        style: { pointerEvents: "none" },
        PaperProps: { style: { pointerEvents: "auto" }, sx: { position: "fixed", bottom: 10, right: "50%" } },
    });

    const newContactDialog = useDialog(close => <ContactDialog close={close} useCustomerStore={props.useCustomerStore} />, "sm",
        { dialogProps: contactDialogProps(), draggable: true, dialogPool, offsetDirectionMultiplierY: -1 });
    const editContactDialog = useDialog(close => <StoreWrapper useStore={() => useNoteListStore(p => ({ selectedItem: p.selectedItem }))}
        render={store => <ContactDialog close={close} useCustomerStore={props.useCustomerStore} serverData={store.selectedItem!} />} />, "sm",
        { dialogProps: contactDialogProps(), draggable: true, dialogPool, offsetDirectionMultiplierY: -1 });

    function editCustomerNote() {
        createAxios().get("customers/customerNotes", { params: { "customerId": customerId } });
        editContactDialog.show();
    }

    return <>
        {newContactDialog.render()}
        {editContactDialog.render()}
        <Typography variant="h6">Contacts</Typography>
        {renderListStore(useNoteListStore, {
            onDoubleClick: editCustomerNote, header: listHeaderItems([
                ["edit", () => <Button color="edit" disabled={selectedNote == null} title="Edit" onClick={editCustomerNote}><Edit /></Button>],
                ["add", () => <Button color="add" disabled={customerId == null || !canManage} title="Add" onClick={() => newContactDialog.show()}><Add /></Button>],
            ]),
            getRowClass: p => p.isPriority ? ["greenRibbon"] : [],
        })}
    </>;
}
